import { useEffect, useState } from "react";
import { useDistrictGuard } from "./useDistrictGuard";
import { getDistrictSetting, setDistrictSetting } from "./api";

// ── Helpers ───────────────────────────────────────────────────────────────────
export const uid = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

export function parseMinutes(str) {
  if (!str) return 9999;
  const s = str.trim().toLowerCase();
  const m12 = s.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/);
  if (m12) {
    let h = parseInt(m12[1], 10);
    const min = parseInt(m12[2], 10);
    if (m12[3] === "pm" && h !== 12) h += 12;
    if (m12[3] === "am" && h === 12) h = 0;
    return h * 60 + min;
  }
  const m24 = s.match(/^(\d{1,2}):(\d{2})$/);
  if (m24) return parseInt(m24[1], 10) * 60 + parseInt(m24[2], 10);
  return 9999;
}

// ── Storage keys — 7-day era ──────────────────────────────────────────────────
const LOCAL_KEYS = {
  domingo:   "pastoral.schedule.domingo.v3",
  lunes:     "pastoral.schedule.lunes.v3",
  martes:    "pastoral.schedule.martes.v3",
  miercoles: "pastoral.schedule.miercoles.v3",
  jueves:    "pastoral.schedule.jueves.v3",
  viernes:   "pastoral.schedule.viernes.v3",
  sabado:    "pastoral.schedule.sabado.v3",
};

// Legacy localStorage keys from old 3-type system — one-time migration on first read
const LEGACY_LOCAL_KEYS = {
  domingo:   "pastoral.schedule.domingo.v3", // same key — no copy needed
  lunes:     "pastoral.schedule.feriado.v3", // old "feriado" becomes "lunes"
  martes:    "pastoral.schedule.habil.v3",   // old "habil" becomes weekdays
  miercoles: "pastoral.schedule.habil.v3",
  jueves:    "pastoral.schedule.habil.v3",
  viernes:   "pastoral.schedule.habil.v3",
  sabado:    null,                           // no legacy — starts empty by design
};

// Supabase district_settings keys
const SETTINGS_KEYS = {
  domingo:   "schedule_template_domingo",
  lunes:     "schedule_template_lunes",
  martes:    "schedule_template_martes",
  miercoles: "schedule_template_miercoles",
  jueves:    "schedule_template_jueves",
  viernes:   "schedule_template_viernes",
  sabado:    "schedule_template_sabado",
};

// Legacy Supabase keys from old 3-type system — fallback during first sync
const LEGACY_SETTINGS_KEYS = {
  domingo:   null,                          // "schedule_template_domingo" unchanged
  lunes:     "schedule_template_feriado",
  martes:    "schedule_template_habil",
  miercoles: "schedule_template_habil",
  jueves:    "schedule_template_habil",
  viernes:   "schedule_template_habil",
  sabado:    null,
};

// ── Local read/write ──────────────────────────────────────────────────────────
function readLocalBlocks(dayType) {
  const id = dayType.id;
  try {
    // Try new day-specific v3 key first
    const raw = localStorage.getItem(LOCAL_KEYS[id]);
    if (raw !== null) {
      const p = JSON.parse(raw);
      if (Array.isArray(p)) return p;
    }
    // One-time migration from old 3-type keys (only when the key differs)
    const legacyKey = LEGACY_LOCAL_KEYS[id];
    if (legacyKey && legacyKey !== LOCAL_KEYS[id]) {
      const rawLegacy = localStorage.getItem(legacyKey);
      if (rawLegacy !== null) {
        const p = JSON.parse(rawLegacy);
        if (Array.isArray(p) && p.length > 0) {
          localStorage.setItem(LOCAL_KEYS[id], rawLegacy); // promote to new key
          return p;
        }
      }
    }
  } catch {}
  return dayType.defaults;
}

function writeLocalBlocks(dayType, blocks) {
  localStorage.setItem(LOCAL_KEYS[dayType.id], JSON.stringify(blocks));
}

const templateEvent = (id) => `scheduleTemplate:updated:${id}`;

// ── Hook ──────────────────────────────────────────────────────────────────────
export function useDayTypeSchedule(dayType) {
  const { districtId } = useDistrictGuard();
  const [blocks, setBlocks] = useState(() => readLocalBlocks(dayType));

  // Hydrate from Supabase on mount — remote wins over stale local
  useEffect(() => {
    if (!districtId) return;
    const id          = dayType.id;
    const settingsKey = SETTINGS_KEYS[id];
    const legacyKey   = LEGACY_SETTINGS_KEYS[id];

    getDistrictSetting(districtId, settingsKey, null)
      .then(async (remote) => {
        if (Array.isArray(remote) && remote.length > 0) {
          writeLocalBlocks(dayType, remote);
          setBlocks(remote);
          return;
        }
        // One-time migration: try legacy Supabase key
        if (!legacyKey) return;
        const legacyRemote = await getDistrictSetting(districtId, legacyKey, null).catch(() => null);
        if (Array.isArray(legacyRemote) && legacyRemote.length > 0) {
          writeLocalBlocks(dayType, legacyRemote);
          setBlocks(legacyRemote);
          // Save under new key so next load skips migration
          setDistrictSetting(districtId, settingsKey, legacyRemote).catch(() => {});
        }
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [districtId, dayType.id]);

  // Cross-tab / cross-component sync
  useEffect(() => {
    const ev   = templateEvent(dayType.id);
    const sync = () => setBlocks(readLocalBlocks(dayType));
    window.addEventListener(ev, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(ev, sync);
      window.removeEventListener("storage", sync);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayType.id]);

  const persist = (next, resort = false) => {
    const final = resort
      ? [...next].sort((a, b) => parseMinutes(a.time) - parseMinutes(b.time))
      : next;
    writeLocalBlocks(dayType, final);
    setBlocks(final);
    window.dispatchEvent(new CustomEvent(templateEvent(dayType.id)));
    if (districtId) {
      setDistrictSetting(districtId, SETTINGS_KEYS[dayType.id], final).catch(() => {});
    }
  };

  return {
    blocks,
    addBlock:    ()                    => persist([...blocks, { id: uid(), time: "", activity: "" }]),
    updateBlock: (id, changes, resort) => persist(blocks.map((b) => (b.id === id ? { ...b, ...changes } : b)), !!resort),
    removeBlock: (id)                  => persist(blocks.filter((b) => b.id !== id)),
    resetBlocks: ()                    => persist(dayType.defaults.map((d) => ({ ...d, id: uid() }))),
  };
}
